import React, { useMemo, useState } from "react";
import { t } from "../i18n";
import { PhoneLink } from "./orderKit";

/** Picture for a fleet vehicle code (canonical codes first, then legacy names by keyword). */
export function vehicleEmoji(code) {
  const c = String(code || "").toUpperCase();
  const exact = {
    MOTORCYCLE: "🏍️",
    BAJAJ: "🛺",
    THREE_WHEELER: "🛺",
    PASSENGER_CAR: "🚗",
    MINIVAN: "🚐",
    CARGO_VAN: "🚐",
    BOX_TRUCK: "🚚",
    MEDIUM_TRUCK: "🚚",
    FLATBED_TRUCK: "🛻",
    HEAVY_TRUCK: "🚛",
  };
  if (exact[c]) return exact[c];
  if (c.includes("MOTOR")) return "🏍️";
  if (c.includes("BIKE") || c.includes("BICYCLE")) return "🚲";
  if (c.includes("TRUCK")) return "🚚";
  if (c.includes("VAN")) return "🚐";
  if (c.includes("CAR")) return "🚗";
  return "🚙";
}

/** One collapsible card: header always shows a live summary so the merchant rarely needs to open it. */
function SetupPanel({ id, icon, title, tag, summary, badge, badgeTone = "muted", open, onToggle, children }) {
  const bodyId = `${id}-body`;
  return (
    <div className={`route-setup__panel${open ? " is-open" : ""}`}>
      <button type="button" className="route-setup__head" aria-expanded={open} aria-controls={bodyId} onClick={onToggle}>
        <span className="route-setup__icon" aria-hidden="true">
          {icon}
        </span>
        <span className="route-setup__text">
          <span className="route-setup__title">
            {title}
            {tag ? <span className="route-setup__tag">{tag}</span> : null}
          </span>
          <span className="route-setup__summary">{summary}</span>
        </span>
        {badge != null ? <span className={`route-setup__badge route-setup__badge--${badgeTone}`}>{badge}</span> : null}
        <span className="route-setup__chevron" aria-hidden="true" />
      </button>
      <div className="route-setup__body" id={bodyId} role="region" aria-label={title}>
        <div className="route-setup__inner">
          <div className="route-setup__content">{children}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * Planned-route extras as an accordion: add unassigned orders, vehicle type, optional vehicle standards.
 * All panels start collapsed and only one is open at a time, so the confirm bar stays close.
 */
export function RouteSetupAccordion({
  idPrefix,
  showVehicle,
  loading,
  unassignedOrders,
  unassignedLoading,
  unassignedError,
  onAddOrder,
  onLocateOrder,
  vehicleTypes,
  vehicleStandards,
  catalogError,
  vehicleType,
  onVehicleTypeChange,
  requiredVehicleStandards,
  onToggleVehicleStandard,
  onClearVehicleStandards,
}) {
  const [openPanel, setOpenPanel] = useState(null);
  const toggle = (key) => setOpenPanel((cur) => (cur === key ? null : key));

  const standardOptions = useMemo(
    () =>
      [...vehicleStandards].sort((a, b) =>
        String(a.title || a.code).localeCompare(String(b.title || b.code), undefined, { sensitivity: "base" })
      ),
    [vehicleStandards]
  );

  const selectedType = vehicleTypes.find((vt) => vt.code === vehicleType);
  const selectedStandardTitles = standardOptions
    .filter((s) => requiredVehicleStandards.has(s.code))
    .map((s) => s.title || s.code);

  const waiting = unassignedOrders.length;
  let unassignedSummary;
  if (unassignedLoading) unassignedSummary = t("draft.loading_unassigned");
  else if (unassignedError) unassignedSummary = unassignedError;
  else if (waiting === 0) unassignedSummary = t("setup.unassigned_none");
  else if (waiting === 1) unassignedSummary = t("setup.unassigned_one");
  else unassignedSummary = t("setup.unassigned_n", { n: waiting });

  const standardsSummary =
    selectedStandardTitles.length === 0 ? t("setup.standards_none") : selectedStandardTitles.join(", ");

  return (
    <section className="route-setup">
      <div className="route-setup__intro">
        <h3>{t("setup.title")}</h3>
        <span>{t("setup.hint")}</span>
      </div>

      <SetupPanel
        id={`${idPrefix}-unassigned`}
        icon="📦"
        title={t("draft.add_from_unassigned")}
        summary={unassignedSummary}
        badge={unassignedLoading || unassignedError ? null : waiting}
        badgeTone={waiting > 0 ? "accent" : "muted"}
        open={openPanel === "unassigned"}
        onToggle={() => toggle("unassigned")}
      >
        {unassignedError ? <p className="route-setup__error">{unassignedError}</p> : null}
        {!unassignedLoading && !unassignedError && waiting === 0 ? (
          <p className="route-setup__empty">{t("draft.no_unassigned")}</p>
        ) : null}
        {waiting > 0 && onLocateOrder ? <p className="route-setup__tip">🗺️ {t("plan.hint")}</p> : null}
        {waiting > 0 ? (
          <ul className="route-setup__orders">
            {unassignedOrders.map((o) => (
              <li key={o.id} className="route-setup__order">
                <span className="route-setup__order-text">
                  <strong>#{o.id}</strong>
                  <span>{o.deliveryAddress || t("draft.no_address")}</span>
                  <PhoneLink phone={o.clientPhone} />
                </span>
                {onLocateOrder && Number.isFinite(parseFloat(o.deliveryLatitude)) && Number.isFinite(parseFloat(o.deliveryLongitude)) ? (
                  <button
                    type="button"
                    className="route-setup__locate"
                    onClick={() => onLocateOrder(o)}
                    title={t("plan.locate")}
                    aria-label={t("plan.locate")}
                  >
                    📍
                  </button>
                ) : null}
                <button
                  type="button"
                  className="route-setup__add"
                  onClick={() => onAddOrder(o.id)}
                  disabled={loading}
                  title={t("draft.add_to_end")}
                >
                  {loading ? "…" : `+ ${t("draft.add_to_end")}`}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </SetupPanel>

      {showVehicle ? (
        <>
          <SetupPanel
            id={`${idPrefix}-vtype`}
            icon={vehicleEmoji(vehicleType)}
            title={t("vehicle.type")}
            summary={selectedType ? selectedType.title || selectedType.code : t("setup.choose_vehicle")}
            badge={selectedType ? "✓" : null}
            badgeTone="success"
            open={openPanel === "vtype"}
            onToggle={() => toggle("vtype")}
          >
            {catalogError ? <p className="route-setup__empty">{catalogError}</p> : null}
            <div className="route-setup__tiles" role="radiogroup" aria-label={t("vehicle.type")}>
              {vehicleTypes.map((vt) => {
                const active = vt.code === vehicleType;
                return (
                  <button
                    key={vt.code}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    className={`route-setup__tile${active ? " is-active" : ""}`}
                    onClick={() => {
                      onVehicleTypeChange(vt.code);
                      setOpenPanel(null);
                    }}
                  >
                    <span className="route-setup__tile-emoji" aria-hidden="true">
                      {vehicleEmoji(vt.code)}
                    </span>
                    <span className="route-setup__tile-label">{vt.title || vt.code}</span>
                  </button>
                );
              })}
            </div>
          </SetupPanel>

          <SetupPanel
            id={`${idPrefix}-vstd`}
            icon="🛡️"
            title={t("vehicle.standards")}
            tag={t("setup.optional")}
            summary={standardsSummary}
            badge={selectedStandardTitles.length > 0 ? selectedStandardTitles.length : null}
            badgeTone="accent"
            open={openPanel === "vstd"}
            onToggle={() => toggle("vstd")}
          >
            {standardOptions.length === 0 ? (
              <p className="route-setup__empty">{t("vehicle.none_loaded")}</p>
            ) : (
              <div className="route-setup__chips" role="group" aria-label={t("vehicle.standards")}>
                {standardOptions.map((s) => {
                  const on = requiredVehicleStandards.has(s.code);
                  return (
                    <button
                      key={s.code}
                      type="button"
                      aria-pressed={on}
                      className={`route-setup__chip${on ? " is-on" : ""}`}
                      title={s.description || undefined}
                      onClick={() => onToggleVehicleStandard(s.code)}
                    >
                      <span className="route-setup__chip-mark" aria-hidden="true">
                        {on ? "✓" : "+"}
                      </span>
                      {s.title || s.code}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="route-setup__foot">
              <span>{t("vehicle.standards_note")}</span>
              {requiredVehicleStandards.size > 0 ? (
                <button type="button" className="route-setup__clear" onClick={onClearVehicleStandards}>
                  {t("vehicle.clear_selection")}
                </button>
              ) : null}
            </div>
          </SetupPanel>
        </>
      ) : null}
    </section>
  );
}
